import moment, { Moment } from 'moment';

export const COUNTRIES = {
  SG: 'SG',
  MY: 'MY',
  AU: 'AU',
  TH: 'TH',
  HK: 'HK',
} as const;
export type ECountry = Values<typeof COUNTRIES>;

export const MAX_QUARTERS = 4;
export const MAX_CHART_COLUMNS = 12;
export const COLOR_WHEEL = ['#D3ADF7', '#87E8DE', '#95DE64', '#FFEC3D', '#69C0FF', '#52C41A', '#FF7A45', '#FF85C0'];
export const ALL = 'all';
export const ALL_ACTIVE = 'all-active';
export const NOT_AVAILABLE = 'not-available';
export const BAR_CHART_LABEL_WIDTH = 4.2;
export const NO_ACTIVE_LOYALTY_CAMPAIGN = 'No campaigns in this date range';
export const STAFF_APP_URL_SG = 'https://staff.shopback.sg';
export const STAFF_APP_URL_MY = 'https://staff.shopback.my';
export const STAFF_APP_URL_AU = 'https://staff.shopback.com.au';
export const STAFF_APP_URL_HK = 'https://staff.shopback.com.hk';
export const LEAD_INBOUND_URL = 'https://business.shopback.com';
export const APP_SG_URL = 'business.shopback.sg';
export const APP_AU_URL = 'business.shopback.com.au';
export const APP_TH_URL = 'business.shopback.co.th';
export const APP_MY_URL = 'business.shopback.my';
export const APP_HK_URL = 'business.shopback.com.hk';
export const SUPPORT_EMAIL_SG = 'instore-sg@shopback.com';
export const SUPPORT_EMAIL_AU = 'instore-au@shopback.com';
export const MERCHANT_FAQ_URL = 'https://shopback.force.com/merchanthelpcenter/s/';

// Used when data is to range from selected date to anything before
export const EARLIEST_START_DATE = '2014-01-01';
export const DEFAULT_DATE_RANGE: [Moment, Moment] = [moment().subtract(31, 'days'), moment().subtract(1, 'days')];
export const FORMATTED_DEFAULT_DATE_RANGE: [string, string] = [
  DEFAULT_DATE_RANGE[0].format('YYYY-MM-DD'),
  DEFAULT_DATE_RANGE[1].format('YYYY-MM-DD'),
];
export const VAR_DATE_RANGE_LIMIT = 93;

// For CRM portal form validation
// www.unicode.org/Public/UCD/latest/ucd/emoji/emoji-data.txt
export const INVALID_CHAR_REGEX = /[^\x20-\x7F]/gu;
export const OCCUPIED_DATE_STRINGS = [
  // '2021-07-20', // Hari Raya Haji
  // '2021-08-09', // National Day
  // '2021-10-30', // Halloween
  // '2021-11-04', // Deepavali
  // '2021-12-24', // Christmas Eve
  '2021-12-25', // Christmas Day
  '2022-12-25', // Christmas Day
  '2023-12-25', // Christmas Day
  '2024-12-25', // Christmas Day
];

// https://fettblog.eu/tidy-typescript-avoid-enums/
// Get to the const values of any object
// Example: typeof ERROR_CODE.CONNECTION === EErrorCode
type Values<T> = T[keyof T];

export const ERROR_CODE = {
  CONNECTION: 'connection failed',
  AUTH: 'auth failure',
  INVALID_PARAMETER: 'INVALID_PARAMETER',
  BELOW_MIN_REFUND: 'BELOW_MIN_REFUND_AMOUNT',
} as const;
export type EErrorCode = Values<typeof ERROR_CODE>;

export const DATE_UNITS = {
  QUARTERLY: 'quarter',
  MONTHLY: 'month',
  WEEKLY: 'week',
  DAILY: 'day',
} as const;

export const PRODUCT_TYPES = {
  SBPAY: 'sbpay',
  CARD: 'clo',
  ALL: 'all',
  VOUCHER: 'voucher',
};

export const TRANSACTION_TYPES = {
  ALL: 'any',
  SBPAY: 'sbpay',
  CARD: 'all',
  VOUCHER: 'credit-card',
};

export type EDateUnit = Values<typeof DATE_UNITS>;

export const USER_STATES = {
  NOT_AUTHENTICATED: false,
  AUTHENTICATING: undefined,
  AUTHENTICATED: true,
} as const;
export type EUserState = Values<typeof USER_STATES>;

export const QUERY_STATES = {
  QUERY_NOT_STARTED: 'not queried',
  QUERY_START: 'making query',
  QUERY_FAILED: 'query failed',
  QUERY_NO_DATA: 'no data',
} as const;
export type EQueryState = Values<typeof QUERY_STATES>;

export const VALUE_TYPES = {
  NUMBER: 'number',
  CURRENCY: 'currency',
} as const;
export type EValueType = Values<typeof VALUE_TYPES>;

export const PAGE_TYPES = {
  HOME: 'HOME',
  AUTH_HOME: 'AUTH HOME',
} as const;
export type EPageType = Values<typeof PAGE_TYPES>;

export const PASSWORD_RESET_STATES = {
  NOT_CHECKED: 'NOT_CHECKED',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;
export type EPasswordResetState = Values<typeof PASSWORD_RESET_STATES>;

export const LOYALTY_CAMPAIGN_MODES = {
  AMOUNT: 'amount',
  FREQUENCY: 'frequency',
  HYBRID: 'hybrid',
} as const;
export type ELoyaltyCampaignMode = Values<typeof LOYALTY_CAMPAIGN_MODES>;

export const CUSTOMER_SEGMENTS = {
  ONE_ORDER: '1-order',
  REPEAT: 'repeat',
  INACTIVE: 'inactive',
} as const;
export type ECustomerSegment = Values<typeof CUSTOMER_SEGMENTS>;
export const isCustomerSegment = (s: string): s is ECustomerSegment =>
  (Object.values(CUSTOMER_SEGMENTS) as string[]).includes(s);

export const LANDING_TYPES = {
  OUTLET: 'outlet',
  VOUCHER: 'voucher',
} as const;
export type ELandingType = Values<typeof LANDING_TYPES>;

export const isOneOfErrors = (value: unknown) => Object.values(ERROR_CODE).find((val) => val === value);

export const isOneOfQueryStates = (value: unknown) => Object.values(QUERY_STATES).find((val) => val === value);

// Type guards
export const unionIsQueryState = <T>(res: T | EQueryState): res is EQueryState => isOneOfQueryStates(res) !== undefined;
export const unionIsErrorCode = <T>(res: T | EErrorCode): res is EErrorCode => isOneOfErrors(res) !== undefined;

// Breakpoints
export const BREAKPOINTS = {
  XS: 480,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1600,
};

export const CASE_STUDIES_URL =
  'https://cloud.shopback.com/image/upload/v1640155941/app-assets/SBGO/Merchant%20Portal/ShopBack_GO_Case_Study.pdf';

export const HELP_CENTRE_URL = 'https://shopback.force.com/merchanthelpcenter/s/';
export const HELP_CENTRE_URL_SG = 'https://shopback.my.site.com/merchanthelpcenter/s/';
export const HELP_CENTRE_URL_AU = 'https://shopback.my.site.com/merchanthelpcenterAU/s/';
export const HELP_CENTRE_URL_TH = 'https://shopback.my.site.com/merchanthelpcenterTH/s/';
export const HELP_CENTRE_URL_MY = 'https://shopback.my.site.com/merchanthelpcenterMY/s/';

// Dimensions for Brand Partners Section in Landing Page
export const SCROLL_ITEMS_WIDTHS = {
  SM: 50,
  MD: 100,
  LG: 150,
};

export const SCROLL_ITEMS_MARGINS = {
  SM: 16,
  MD: 30,
  LG: 48,
};

export const ANIMATION_DURATIONS = {
  SM: 60,
  MD: 70,
  LG: 90,
};

export const PAYLATER_IU_SELECT_ID = 'paylater-iu-select';
export const PAYLATER_OUTLET_SELECT_ID = 'paylater-outlet-select';
export const PAYLATER_DATE_RANGE_CONTAINER_ID = 'paylater-date-range-container';
export const TODAY_DATE_RANGE: [Moment, Moment] = [moment().startOf('day'), moment()];
export const THIS_WEEK_DATE_RANGE: [Moment, Moment] = [moment().startOf('week'), moment()];
export const THIS_MONTH_DATE_RANGE: [Moment, Moment] = [moment().startOf('month'), moment()];
export const PAYLATER_DEFAULT_DATE_RANGE: [Moment, Moment] = [moment().subtract(30, 'days'), moment()];

export const PAYLATER_ORDER_TYPE = {
  INSTORE: 'INSTORE',
  ONLINE: 'ONLINE',
};

export const PAYLATER_ORDER_STATUSES = {
  COMPLETED: 'COMPLETED',
  REFUNDED: 'REFUNDED',
  REVERSED: 'REVERSED',
  ACTIVE: 'ACTIVE',
};

export const VALID_PAYLATER_ORDER_STATUSES = [
  PAYLATER_ORDER_STATUSES.ACTIVE,
  PAYLATER_ORDER_STATUSES.REFUNDED,
  PAYLATER_ORDER_STATUSES.COMPLETED,
  PAYLATER_ORDER_STATUSES.REVERSED,
];

export const PAYLATER_ORIGIN = {
  HOOLAH: 'HOOLAH',
  SHOPBACK: 'SHOPBACK',
};

export const PAYLATER_FALL_BACK_IMG =
  'data:image/png;base64,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';

export const DEFAULT_PORTAL_CONFIG = {
  isMicVisible: true,
  isVarVisible: true,
};

// Utility function to check if current domain is a global domain
export const isGlobalDomain = () => {
  /* global window */
  return (
    window.location.hostname.includes('business-mt-staging.shopback.com') ||
    window.location.hostname.includes('portal.shopback.com') ||
    window.location.hostname.includes('172.16.30')
  );
};
